// Production (owner) — the run pipeline: advance status + record finished goods (made/sent/kept).
// Same panel the manufacturer uses, via the /run-status and /complete endpoints.
import { useState } from "react";
import type { Run, RunStatus } from "@shared/types.ts";
import { computeRequirements, cansPerBarrel, skuRecipeKey, componentsForRun } from "@shared/engine.ts";
import { useData } from "../state/store.tsx";
import { api } from "../lib/api.ts";
import { ProductionPanel } from "../components/ProductionPanel.tsx";

export default function Production({ focusRunId }: { focusRunId?: string | null }) {
  const { data, reload } = useData();
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const skuName = (id: string) => data.skus.find((s) => s.id === id)?.name || id;
  const skuMeta = (id: string) => {
    const s = data.skus.find((x) => x.id === id);
    return { name: s?.name || id, flavour: s?.flavour, strength: s?.strength, imageUrl: s?.imageUrl, productType: s?.productType };
  };
  const cpb = cansPerBarrel(data.business.conversions);
  const skuRecipe = (id: string) => {
    const s = data.skus.find((x) => x.id === id);
    if (!s) return null;
    const lines = data.recipes[skuRecipeKey(s)] || [];
    if (!lines.length) return null;
    return lines.map((l) => ({ ingredient: l.ingredient, lbsPerCan: Number(l.lbsPerCan) || 0, lbsPerBbl: (Number(l.lbsPerCan) || 0) * cpb }));
  };
  // Latest arrival date across the run's ingredient order — received date if in, else expected.
  const ingredientEta = (run: Run): string | null => {
    const orders = data.orders.filter((o) => o.runId === run.id || (run.orderId && o.id === run.orderId));
    const dates = orders.flatMap((o) => o.lines).filter((l) => l.toOrderLbs > 0)
      .map((l) => l.received?.dateReceived || (l.received?.confirmedAt ? new Date(l.received.confirmedAt).toISOString().slice(0, 10) : "") || l.expectedDelivery)
      .filter(Boolean) as string[];
    return dates.length ? dates.sort().slice(-1)[0] : null;
  };
  // Of the run's ingredients, how many are covered — by on-hand raw-material stock (which includes
  // received deliveries), canner-supplied, or otherwise no longer needing more.
  const ingredientProgress = (run: Run) => {
    const rows = computeRequirements(data, run.items).filter((r) => r.requiredLbs > 0.0001);
    if (!rows.length) return null;
    const covered = (r: (typeof rows)[number]) => r.provided || r.onHandLbs >= r.requiredLbs - 1e-6;
    return { have: rows.filter(covered).length, total: rows.length, missing: rows.filter((r) => !covered(r)).map((r) => r.ingredient) };
  };
  const containerWord = (run: Run) => {
    const sku = run.items.map((i) => data.skus.find((s) => s.id === i.skuId)).find(Boolean);
    const t = sku?.productType || "can";
    return t === "bottle" ? "Bottles" : t === "gummy" ? "Gummy packs" : "Cans";
  };
  const onSetStatus = async (runId: string, status: RunStatus) => {
    setBusy(true);
    try { await api.runStatus({ businessId: data.business.id, runId, status }); await reload(); } finally { setBusy(false); }
  };
  const onComplete = async (runId: string, items: { skuId: string; made: number; sent: number; kept: number }[], notes: string) => {
    setBusy(true);
    try { await api.complete({ businessId: data.business.id, runId, items, notes }); await reload(); } finally { setBusy(false); }
  };
  const onUpdate = async (runId: string, patch: { scheduledDate?: string; readyDate?: string; poNumber?: string; batchNumber?: string; notes?: string }) => {
    setBusy(true);
    try { await api.runUpdate({ businessId: data.business.id, runId, patch }); await reload(); } finally { setBusy(false); }
  };
  const onSchedule = async (runId: string, action: "propose" | "confirm", date?: string, note?: string) => {
    setBusy(true);
    try { await api.runSchedule({ businessId: data.business.id, runId, action, date, note }); await reload(); } finally { setBusy(false); }
  };
  return <ProductionPanel runs={data.runs} role="owner" focusRunId={focusRunId} skuName={skuName} skuMeta={skuMeta} skuRecipe={skuRecipe} unit={data.business.unitLabel} busy={busy} ingredientProgress={ingredientProgress} ingredientEta={ingredientEta} containerWord={containerWord} components={(run) => componentsForRun(data, run)} onSetStatus={onSetStatus} onComplete={onComplete} onUpdate={onUpdate} onSchedule={onSchedule}
    onNudge={async (runId, title, message) => api.nudge({ businessId: data.business.id, runId, title, message })} />;
}
